<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import _ from 'lodash'
import { useI18n } from 'vue-i18n'
import { useIncidentStore } from '@/store/incident.js'
const { t } = useI18n()

const incidentStore = useIncidentStore()
const props = defineProps({
  defaultIncludeInEntities: { type: Array, default: () => [] },
  defaultStakeholders: { type: Array, default: () => [] },
  defaultColor: { type: String, default: '#2980B9' },
})
const availableIncludeInEntities = ref([])
const compiledIncludeInEntities = computed(() => {
  return availableIncludeInEntities.value.map(entity => ({
    title: t('incidents.genericProperties.include-in-entities.' + entity),
    value: entity,
  }))
})
const availableStakeholders = ref([])
const compiledStakeholderItems = computed(() => {
  return availableStakeholders.value.map(stakeholder => ({
    title: t('incidents.genericProperties.stakeholder-elements.' + stakeholder),
    value: stakeholder,
  }))
})
const pickedStakeholders = ref([])
const availableDefaultSwatches = [
  ['#2980B9', '#3498DB', '#00BCD4'],
  ['#27AE60', '#2ECC71', '#1ABC9C'],
  ['#C0392B', '#E74C3C', '#D35400'],
  ['#8E44AD', '#9B59B6', '#E91E63'],
  ['#E67E22', '#F39C12', '#F1C40F'],
]

const pickedIncludeInEntities = ref([])
const renderColor = ref()

const model = defineModel({ type: Object, default: undefined })

watch(
  props.defaultIncludeInEntities,
  () => {
    if (props.defaultIncludeInEntities === null) return
    pickedIncludeInEntities.value = props.defaultIncludeInEntities
  },
  { immediate: true, deep: true }
)

watch(
  props.defaultStakeholders,
  () => {
    if (props.defaultStakeholders === null) return
    pickedStakeholders.value = props.defaultStakeholders
  },
  { immediate: true, deep: true }
)

watch(
  props.defaultColor,
  () => {
    if (props.defaultColor === null) return
    renderColor.value = props.defaultColor
  },
  { immediate: true, deep: true }
)

watch(
  [pickedIncludeInEntities, pickedStakeholders, renderColor],
  () => {
    model.value = {
      displayConfiguration: {
        includeIn: pickedIncludeInEntities.value,
        displaySettingEntityOverrides: [],
        displaySettings: { renderColor: renderColor.value },
      },
      stakeholder: pickedStakeholders.value,
    }
  },
  { deep: true }
)

async function loadPossibleDisplayConfigurationsAndStakeholders() {
  availableIncludeInEntities.value = await incidentStore.loadDisplayConfigurations()
  availableStakeholders.value = await incidentStore.loadAvailableStakeholders()
}
function propagateModel() {
  if (!model.value) {
    renderColor.value = '#2980B9'
    return
  }
  if (!_.isEqual(pickedIncludeInEntities.value, model.value?.displayConfiguration?.includeIn || []))
    pickedIncludeInEntities.value = model.value?.displayConfiguration?.includeIn || []
  if (!_.isEqual(pickedStakeholders.value, model.value.stakeholder || [])) pickedStakeholders.value = model.value.stakeholder || []
  if (renderColor.value !== model.value?.displayConfiguration?.displaySettings?.renderColor)
    renderColor.value = model.value?.displayConfiguration?.displaySettings?.renderColor || '#2980B9'
}
watch(
  model,
  () => {
    propagateModel()
  },
  { deep: true }
)

onMounted(async () => {
  propagateModel()
  await loadPossibleDisplayConfigurationsAndStakeholders()
})
</script>

<template>
  <v-row
    justify="center"
    align="center"
  >
    <v-col cols="12">
      <v-autocomplete
        v-model="pickedIncludeInEntities"
        max-width="600px"
        closable-chips
        min-width="400px"
        :label="$t('incidents.genericProperties.include-in')"
        variant="outlined"
        multiple
        :items="compiledIncludeInEntities"
        chips
        hide-details
      />
    </v-col>
    <v-col cols="12">
      <v-autocomplete
        v-model="pickedStakeholders"
        max-width="600px"
        closable-chips
        min-width="400px"
        :label="$t('incidents.genericProperties.stakeholders')"
        variant="outlined"
        multiple
        :items="compiledStakeholderItems"
        chips
        hide-details
      />
    </v-col>
    <v-col cols="12">
      <v-label>{{ $t('incidents.genericProperties.render-color-picker') }}</v-label>
      <v-color-picker
        v-model="renderColor"
        class="my-2"
        mode="hex"
        min-width="300px"
        hide-inputs
        show-swatches
        :swatches="availableDefaultSwatches"
        swatches-max-height="100"
        hide-canvas
      />
    </v-col>
  </v-row>
</template>

<style scoped></style>
